import HomeIcon from '@src/svgs/HomeIcon';
import { Fragment, useState } from 'react';
import styled from 'styled-components';

import BreadcrumbFolderMenu from './BreadcrumbFolderMenu';
import {
  dataTransferHasStoryIds,
  readStoryIdsDragPayload,
} from './dragPayload';

import type { Folder } from '@src/types.ts/story';
import type { DragEvent, FC } from 'react';

type Props = {
  trail: Folder[];
  onNavigate: (folderId: string | null) => void;
  onStoriesDropped?: (storyIds: string[], folderId: string | null) => void;
  /** Folder actions for the current (deepest) segment's dropdown. When
   *  provided, the last segment becomes an actions menu instead of plain text. */
  onCopyFolderLink?: (folder: Folder) => void;
  onRenameFolder?: (folder: Folder) => void;
  onDeleteFolder?: (folder: Folder) => void;
  onNewSubfolder?: () => void;
};

const FolderBreadcrumb: FC<Props> = ({
  trail,
  onNavigate,
  onStoriesDropped,
  onCopyFolderLink,
  onRenameFolder,
  onDeleteFolder,
  onNewSubfolder,
}) => {
  const [dropTargetId, setDropTargetId] = useState<string | null | 'none'>(
    'none',
  );

  const dropHandlers = (segmentFolderId: string | null) => {
    if (!onStoriesDropped) return undefined;
    const onDragOver = (event: DragEvent<HTMLButtonElement>) => {
      if (!dataTransferHasStoryIds(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDropTargetId(segmentFolderId);
    };
    const onDragLeave = () => {
      setDropTargetId(prev => (prev === segmentFolderId ? 'none' : prev));
    };
    const onDrop = (event: DragEvent<HTMLButtonElement>) => {
      const ids = readStoryIdsDragPayload(event.dataTransfer);
      setDropTargetId('none');
      if (!ids || ids.length === 0) return;
      event.preventDefault();
      onStoriesDropped(ids, segmentFolderId);
    };
    return { onDragOver, onDragLeave, onDrop };
  };

  return (
    <Crumbs aria-label="Folder breadcrumb" data-testid="folder-breadcrumb">
      <Segment
        onClick={() => onNavigate(null)}
        $active={trail.length === 0}
        data-drop-target={dropTargetId === null || undefined}
        data-testid="folder-breadcrumb-root"
        {...(dropHandlers(null) ?? {})}
      >
        <HomeIcon />
        Workspace
      </Segment>
      {trail.map((folder, idx) => {
        const isLast = idx === trail.length - 1;
        // The current (deepest) folder becomes an actions dropdown so it can be
        // renamed/deleted from here. Ancestors stay click-to-navigate.
        if (
          isLast &&
          onCopyFolderLink &&
          onRenameFolder &&
          onDeleteFolder &&
          onNewSubfolder
        ) {
          return (
            <Fragment key={folder.id}>
              <Separator>/</Separator>
              <BreadcrumbFolderMenu
                folder={folder}
                onCopyLink={onCopyFolderLink}
                onRename={onRenameFolder}
                onDelete={onDeleteFolder}
                onNewSubfolder={onNewSubfolder}
              />
            </Fragment>
          );
        }
        return (
          <Fragment key={folder.id}>
            <Separator>/</Separator>
            <Segment
              $active={isLast}
              onClick={() => (isLast ? undefined : onNavigate(folder.id))}
              data-drop-target={dropTargetId === folder.id || undefined}
              data-testid={`folder-breadcrumb-segment-${folder.id}`}
              {...(dropHandlers(folder.id) ?? {})}
            >
              {folder.name}
            </Segment>
          </Fragment>
        );
      })}
    </Crumbs>
  );
};

export default FolderBreadcrumb;

const Crumbs = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const Segment = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 2px 4px;
  border-radius: var(--radius-button);
  font-family: inherit;
  font-size: inherit;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
  color: ${({ $active }) =>
    $active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  font-weight: ${({ $active }) =>
    $active ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)'};

  &:hover {
    color: var(--text-primary);
  }

  &[data-drop-target] {
    outline: 2px solid var(--accent);
    color: var(--text-primary);
  }
`;

const Separator = styled.span`
  color: var(--text-muted);
`;
